/**
 * ErrorState Component - Reusable error display using shadcn Alert
 * 
 * Provides consistent error states across the application.
 * Replaces inconsistent error message patterns.
 * 
 * @example
 * <ErrorState title="Error" message="Failed to load data" />
 * <ErrorState message="Something went wrong" action={<Button>Retry</Button>} />
 */
import { memo } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import React from 'react';

interface ErrorStateProps {
  title?: string;
  message?: string | React.ReactNode;
  action?: React.ReactNode;
  variant?: 'default' | 'destructive';
  className?: string;
}

export const ErrorState = memo(function ErrorState({
  title = "Error",
  message,
  action,
  variant = "destructive",
  className
}: ErrorStateProps) {
  if (!message) return null;

  return (
    <Alert variant={variant} className={className}>
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        {message}
        {action && <div className="mt-4">{action}</div>}
      </AlertDescription>
    </Alert>
  );
});
